import { useCallback, useMemo } from 'react'

import { NetworkStatus } from '@apollo/client'
import type { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

import { readFragment } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import type { TemplateOwner } from '~publish/components/TemplateCard'

import {
  GOAL_LABELS,
  narrowTags,
  narrowUserTypes,
  STRUCTURE_LABELS,
  TYPE_LABELS,
} from '../labels'
import type { Template } from '../types'
import {
  CustomPostTemplateFields,
  MyPostTemplatesQuery,
} from '../graphql/postTemplates'

export const MY_TEMPLATES_PAGE_SIZE = 50

const COLORS = [
  '255, 203, 49',
  '218, 255, 53',
  '99, 117, 255',
  '248, 149, 151',
  '87, 152, 255',
  '59, 252, 255',
]

export type CustomTemplateVisibility = 'private' | 'internal'

type ApiCustomTemplate = ResultOf<typeof CustomPostTemplateFields>

export type CustomTemplate = Template & {
  owner: TemplateOwner
  visibility: CustomTemplateVisibility
  allowedActions: ApiCustomTemplate['allowedActions']
}

type MyApiTemplate = ApiCustomTemplate & {
  visibility: CustomTemplateVisibility
}

function ownerForVisibility(
  visibility: CustomTemplateVisibility,
): TemplateOwner {
  return visibility === 'private' ? 'mine' : 'team'
}

function mapTemplate(node: MyApiTemplate, index: number): CustomTemplate {
  return {
    id: node.id,
    title: node.title,
    body: node.body,
    description: node.description ?? '',
    emoji: node.emoji ?? '📝',
    type: TYPE_LABELS[node.type] ?? 'Tip',
    goal: GOAL_LABELS[node.goal] ?? 'Engagement',
    structure: STRUCTURE_LABELS[node.structure] ?? 'Single Text',
    userTypes: narrowUserTypes(node.userTypes),
    tags: narrowTags(node.tags),
    featured: node.featured ?? false,
    createdAt: node.createdAt,
    color: COLORS[index % COLORS.length],
    linkedCampaigns: [],
    owner: ownerForVisibility(node.visibility),
    visibility: node.visibility,
    allowedActions: node.allowedActions,
  }
}

export type UseMyPostTemplatesResult = {
  loading: boolean
  error: ErrorLike | undefined
  templates: CustomTemplate[]
  hasMore: boolean
  loadMore: () => void
  loadingMore: boolean
  hasMorePersonal: boolean
  hasMoreTeam: boolean
  loadMorePersonal: () => void
  loadMoreTeam: () => void
  loadingMorePersonal: boolean
  loadingMoreTeam: boolean
}

export type UseMyPostTemplatesOptions = {
  /**
   * When true, the query is held back entirely. Useful for callers that
   * conditionally need custom templates (e.g. only on a public-match miss)
   * since hooks themselves can't be conditional.
   */
  skip?: boolean
}

export function useMyPostTemplates(
  options: UseMyPostTemplatesOptions = {},
): UseMyPostTemplatesResult {
  const { skip = false } = options
  const organizationId = useOrganizationId()
  const querySkip = skip || !organizationId

  // Two server-filtered queries instead of one client-filtered union.
  // `postTemplates` returns public + internal + private together, so
  // its `pageInfo.hasNextPage` reflects the union — which leaves "Load
  // more" sticking around after the user has drained their own
  // templates. The schema only accepts a single `visibility` per
  // request, so we fan out into one query per visibility we care about
  // and union the results client-side.
  const privateQuery = useQuery(MyPostTemplatesQuery, {
    variables: {
      input: {
        organizationId: organizationId ?? '',
        filter: { visibility: 'private' },
      },
      first: MY_TEMPLATES_PAGE_SIZE,
    },
    skip: querySkip,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const internalQuery = useQuery(MyPostTemplatesQuery, {
    variables: {
      input: {
        organizationId: organizationId ?? '',
        filter: { visibility: 'internal' },
      },
      first: MY_TEMPLATES_PAGE_SIZE,
    },
    skip: querySkip,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const privateData =
    privateQuery.dataState === 'complete' ? privateQuery.data : undefined
  const internalData =
    internalQuery.dataState === 'complete' ? internalQuery.data : undefined

  const templates = useMemo(() => {
    const fromPrivate = (privateData?.postTemplates.edges ?? [])
      .map((edge) => readFragment(CustomPostTemplateFields, edge.node))
      .filter((node): node is MyApiTemplate => node.visibility === 'private')
    const fromInternal = (internalData?.postTemplates.edges ?? [])
      .map((edge) => readFragment(CustomPostTemplateFields, edge.node))
      .filter((node): node is MyApiTemplate => node.visibility === 'internal')

    return [...fromPrivate, ...fromInternal]
      .toSorted((a, b) => b.createdAt.localeCompare(a.createdAt))
      .map(mapTemplate)
  }, [privateData, internalData])

  const privateInfo = privateData?.postTemplates.pageInfo
  const internalInfo = internalData?.postTemplates.pageInfo
  const hasMorePersonal = privateInfo?.hasNextPage ?? false
  const hasMoreTeam = internalInfo?.hasNextPage ?? false
  const hasMore = hasMorePersonal || hasMoreTeam
  const error = privateQuery.error ?? internalQuery.error
  const loadingMorePersonal =
    privateQuery.networkStatus === NetworkStatus.fetchMore
  const loadingMoreTeam =
    internalQuery.networkStatus === NetworkStatus.fetchMore
  const loadingMore = loadingMorePersonal || loadingMoreTeam
  // Gate `loading` to the initial fetch only (NetworkStatus.loading is 1).
  // `notifyOnNetworkStatusChange: true` flips Apollo's `loading` true for
  // every network activity — refetch, fetchMore, poll — which would replace
  // the already-rendered grid with the skeleton each time. Consumers want
  // to see the skeleton on first paint only; subsequent updates (e.g. the
  // refetch after a create) should leave the cached list in place.
  const loading =
    privateQuery.networkStatus === NetworkStatus.loading ||
    internalQuery.networkStatus === NetworkStatus.loading

  // Visibility-scoped Load more. Each view paginates its own connection so
  // draining one doesn't stall the other, and switching tabs doesn't leave a
  // "Load more" button that secretly advances the other visibility's cursor.
  const privateFetchMore = privateQuery.fetchMore
  const internalFetchMore = internalQuery.fetchMore
  const loadMorePersonal = useCallback((): void => {
    if (!organizationId || loadingMorePersonal) return
    if (!privateInfo?.hasNextPage || !privateInfo.endCursor) return
    void privateFetchMore({
      variables: {
        input: { organizationId, filter: { visibility: 'private' } },
        first: MY_TEMPLATES_PAGE_SIZE,
        after: privateInfo.endCursor,
      },
    })
  }, [
    organizationId,
    loadingMorePersonal,
    privateInfo?.hasNextPage,
    privateInfo?.endCursor,
    privateFetchMore,
  ])
  const loadMoreTeam = useCallback((): void => {
    if (!organizationId || loadingMoreTeam) return
    if (!internalInfo?.hasNextPage || !internalInfo.endCursor) return
    void internalFetchMore({
      variables: {
        input: { organizationId, filter: { visibility: 'internal' } },
        first: MY_TEMPLATES_PAGE_SIZE,
        after: internalInfo.endCursor,
      },
    })
  }, [
    organizationId,
    loadingMoreTeam,
    internalInfo?.hasNextPage,
    internalInfo?.endCursor,
    internalFetchMore,
  ])

  // Convenience: advance whichever connection still has a next page. Kept for
  // callers that want a single button covering both — view-specific consumers
  // should reach for loadMorePersonal/loadMoreTeam instead.
  const loadMore = useCallback((): void => {
    loadMorePersonal()
    loadMoreTeam()
  }, [loadMorePersonal, loadMoreTeam])

  return {
    loading,
    error,
    templates,
    hasMore,
    loadMore,
    loadingMore,
    hasMorePersonal,
    hasMoreTeam,
    loadMorePersonal,
    loadMoreTeam,
    loadingMorePersonal,
    loadingMoreTeam,
  }
}
